// manage local storage & svelte-store
import { writable, get } from 'svelte/store';
import { makeFromCache, genSpecUid, ViewSpec } from "./viewSpec.js";
import { DupoCallQueue } from './callQueue.js';
import { DupoSystemLog } from "./systemLog.js";
import {
  case1_data,
  case1_spec,
  case2_data,
  case2_spec,
  case3_data,
  case3_spec,
  case4_data,
  case4_spec,
  case5_data,
  case5_spec,
  case6_data,
  case6_spec
} from "../manage/preSpecs.js";
import { getDefaultArtboardPresets, veryDefaultArtboards } from "./component/preference.js";
import { deviceTypes } from "./component/artboard.js";
import { deepcopy } from './utility.js';
import { setData } from './component/data.js';
import { prefDefaults, PrefKeys, ResponsiveDirections } from "./component/preference.js";
import { lossWeightKeys } from "../recommender/loss/evaluate.js";
import { importClingo } from "../recommender/clingo-runner.js"

export const SKEY = {
  DATA: "data",
  LOG: "system-log",
  UIDS: "dupo-uids",
  UNLOCKED: "unlockedSpecs",
  ACTIVE: "activeSpec",
  COLORHISTORY: "colorHistory",
  ARTBOARD_PRESET: "artboardPresets",
  PREFERENCE: "preferences",
  MEMORY: "stateMemory",
  MLT: "more-like-this",
  LLT: "less-like-this",
  QUEUE: "call-queue"
}

// STORAGEs
export const callQueue = writable(new DupoCallQueue());
export const viewSpecs = writable([]);
export const data = writable([]);
export const dataInfo = writable({});
export const activeSpec = writable(-1);
export const unlockedSpecs = writable([]);
export const unlockedSpecsMemory = writable([]);
export const soloUnlock = writable(undefined);
export const currentSpec = writable(null);
export const colorHistory = writable([]);
export const devicePreview = writable(false);
export const currMenu = writable(undefined);
export const menuConfig = writable(undefined);
export const systemLog = writable(new DupoSystemLog());
export const directContext = writable(undefined);
export const currentElementLockGroup = writable([]);

// exploration-related
export const clingoObject = writable();
export const moreLikeThisStack = writable([]);
export const lessLikeThisStack = writable([]);
export const pyodide = writable();
export const wasmLoaded = writable({ clingo: false, pyodide: false });

// preferences
export const defaultArtboardSets = writable(getDefaultArtboardPresets());
export const withPPI = writable(prefDefaults.withPPI);
export const showRuler = writable(prefDefaults.showRuler);
export const unlockNew = writable(prefDefaults.unlockNew);
export const responsiveDir = writable(prefDefaults.responsiveDir);
export const nRecommendations = writable(prefDefaults.nRecommendations);
export const nMoreLikeThis = writable(prefDefaults.nMoreLikeThis);
export const pDrastic = writable(prefDefaults.pDrastic);
export const lossWeight = writable(prefDefaults.lossWeight);
export const rationaleLevel = writable(prefDefaults.rationaleLevel);

// preset handling
export let PRESET = writable();

// simple storage
export const glanceView = writable(false);

// import clingo
export async function loadClingo() {
  clingoObject.set(await importClingo());
  wasmLoaded.update((o) => {
    o.clingo = true;
    return o;
  });
}

export async function loadPyodide_() {
  if (window.loadPyodide) {
    let _p = await window.loadPyodide()
    await _p.loadPackage(["pandas", "numpy", "scipy", "statsmodels"]);
    pyodide.set(_p);
    wasmLoaded.update((o) => {
      o.pyodide = true;
      return o;
    });
  }
}

// DATA
export function getStoredData() {
  // align with ...
  let dataLoad = getFromLocalStorage(SKEY.DATA);
  return dataLoad || undefined;
}

// LOG
export async function getStoredLog() {
  let log = getFromLocalStorage(SKEY.LOG)
  if (log !== undefined && log?.constructor.name !== "Array") log = [log];
  else if (log === null) log = [];
  systemLog.set(new DupoSystemLog(log));
  return log;
}
export function saveLog(logItem) {
  storeToLocalStorageWithBound(SKEY.LOG, logItem, 200);
}

// QUEUE
export function storeQueue(queues, type) {
  let data = {
    type,
    queues
  }
  storeToLocalStorageWithBound(SKEY.QUEUE, data, 100);
}

export function storeToLocalStorageWithBound(key, item, bound) {
  let store = getFromLocalStorage(key);
  if (!store) store = [];
  store.unshift(item);
  store = store.slice(0, bound);
  saveToLocalStorage(key, store);
}


// SPECs & UIDs
export async function getStoredSpecs() {
  let uids = getFromLocalStorage(SKEY.UIDS);
  if (uids) {
    let todelete = [];
    let specs = [];
    if (uids.constructor.name === "Array") {
      specs = uids.map(uid => {
        let s = getFromLocalStorage(uid)
        if (s) { return makeFromCache(s); }
        else {
          todelete.push(uid);
          return null;
        }
      });
      let usedCallIDs_Global = []
      specs.forEach(spec => {
        usedCallIDs_Global.push(...(spec?.history?.map(d => d.callID) || []))
      });
      callQueue.update(queue => { queue.setUsedCallIDs(usedCallIDs_Global); return queue });
    }
    saveToLocalStorage("dupo-uids", uids.filter(uid => !todelete.includes(uid)));
    viewSpecs.set(specs.filter(d => d != null))
  }
}

export function deleteViewSpec(uid) {
  // local storage / svelte storage
  // spec
  localStorage.removeItem(uid);
  viewSpecs.update((specs) => {
    return specs.filter((s) => s.uid !== uid);
  });

  // uids
  let uids = getFromLocalStorage(SKEY.UIDS);
  if (uids?.includes(uids)) {
    saveToLocalStorage(SKEY.UIDS, uids.filter(d => d !== uid));
  }
}

export function getSpec(uid) {
  return getFromLocalStorage(uid);
}

export function storeViewSpec(uid, spec) {
  saveToLocalStorage(uid, spec);
}

export function getUIDs() {
  return getFromLocalStorage(SKEY.UIDS)
}
export function storeUID(uid) {
  let uids = getUIDs() || [];
  if (!uids.includes(uid)) uids.push(uid);
  uids = uids.filter(d => getSpec(d));
  saveToLocalStorage(SKEY.UIDS, uids);
}

const prespecKeys = {
  CASE1: "case1",
  CASE2: "case2",
  CASE3: "case3",
  CASE4: "case4",
  CASE5: "case5",
  CASE6: "case6"
}
export function managePreSpec(key) {
  if (Object.values(prespecKeys).includes(key)) {
    if (get(PRESET) !== key) {
      clearCacheAll();
      PRESET.set(key);
      loadPreSpecs();
      systemLog.update((log) => {
        log.log({ message: "Load a preset spec", spec: key });
        return log;
      })
    }
  }
}

export function loadPreSpecs() {
  let presetName = get(PRESET);
  let dataset = {
    case1: case1_data,
    case2: case2_data,
    case3: case3_data,
    case4: case4_data,
    case5: case5_data,
    case6: case6_data
  }[presetName];
  let prespec = {
    case1: case1_spec,
    case2: case2_spec,
    case3: case3_spec,
    case4: case4_spec,
    case5: case5_spec,
    case6: case6_spec
  }[presetName];
  if (dataset && prespec) {
    dataset = deepcopy(dataset);
    prespec = deepcopy(prespec);
    data.set(dataset);
    dataInfo.set(setData(dataset));
    saveToLocalStorage(SKEY.DATA, dataset);
    let uid = genSpecUid();
    prespec.fronto.name = uid;
    let spec = new ViewSpec(uid, prespec.artboard, prespec.history, {
      fronto: prespec.fronto,
      original_artboard: prespec.original_artboard,
      is_first: true,
      source: undefined
    });
    viewSpecs.set([spec]);
    spec.storeToCache();
    storeUID(uid);
    activeSpec.set(uid);
    unlockedSpecs.set([uid]);
    saveToLocalStorage("activeSpec", uid);
    updateUnlockedSpecs([uid]);
    systemLog.set(new DupoSystemLog([]));
    if (presetName === "case1") {
      colorHistory.set([
        {
          "r": 255,
          "g": 134,
          "b": 29,
          "a": 1
        },
        {
          "r": 205,
          "g": 205,
          "b": 205,
          "a": 1
        },
        {
          "r": 241,
          "g": 241,
          "b": 241,
          "a": 1
        },
        {
          "r": 255,
          "g": 255,
          "b": 255,
          "a": 1
        }
      ]);
    } else if (presetName === "case2") {
      colorHistory.set([
        {
          "r": 0,
          "g": 167,
          "b": 182,
          "a": 1
        }
      ]);
    } else if (presetName === "case3") {
      colorHistory.set([
        { r: 141, g: 141, b: 141, a: 1 },
        { r: 208, g: 0, b: 0, a: 1 },
        { r: 69, g: 206, b: 59, a: 1 },
        { r: 244, g: 140, b: 29, a: 1 },
        { r: 255, g: 211, b: 45, a: 1 },
        { r: 41, g: 181, b: 181, a: 1 },
        { r: 86, g: 19, b: 154, a: 1 },
        { r: 165, g: 165, b: 165, a: 1 },
        { r: 75, g: 75, b: 75, a: 1 },
        { r: 220, g: 220, b: 220, a: 1 },
        { r: 255, g: 255, b: 255, a: 0.6773 }
      ]);
    } else if (presetName === "case4") {
      colorHistory.set([
        { r: 172, g: 192, b: 224, a: 1 },
        { r: 33, g: 0, b: 98, a: 1 },
        { r: 55, g: 129, b: 193, a: 1 },
        { r: 181, g: 56, b: 56, a: 1 },
        { r: 135, g: 211, b: 101, a: 1 },
        { r: 222, g: 191, b: 163, a: 1 },
        { r: 227, g: 81, b: 19, a: 1 },
        { r: 12, g: 80, b: 34, a: 1 },
        { r: 180, g: 140, b: 64, a: 1 }
      ]);
    } else if (presetName === "case6") {
      colorHistory.set([
        { r: 152, g: 0, b: 0, a: 1 },
        { r: 9, g: 142, b: 168, a: 1 },
        { r: 11, g: 112, b: 112, a: 1 },
      ]);
    }
  } else if (dataset) {
    dataset = deepcopy(dataset);
    data.set(dataset);
    dataInfo.set(setData(dataset));
    saveToLocalStorage(SKEY.DATA, dataset);
  }
}

// UNLOCKED
export function getUnlockedSpecs() {
  let unlocks = getFromLocalStorage(SKEY.UNLOCKED) || [];
  if (unlocks == null) {
    unlockedSpecs.set(get(viewSpecs).map((spec) => spec.uid));
  } else {
    unlockedSpecs.set(unlocks);
  }
}

export function updateUnlockedSpecs(uids) {
  unlockedSpecs.set(uids);
  saveToLocalStorage(SKEY.UNLOCKED, uids);
}

export function unlockSpec(uid) {
  let unlocks = get(unlockedSpecs);
  unlocks.push(uid);
  updateUnlockedSpecs(unlocks);
}

export function lockSpec(uid) {
  let unlocks = get(unlockedSpecs);
  unlocks = unlocks.filter(u => u !== uid);
  updateUnlockedSpecs(unlocks);
}

export function setSoloUnlock(uid) {
  soloUnlock.set(uid);
}

export function clearSoloUnlock() {
  soloUnlock.set(undefined);
}

export function setUnlockedSpecsMemory(uids) {
  unlockedSpecsMemory.set(uids);
}


// ACTIVE
export function getActiveSpec() {
  return getFromLocalStorage(SKEY.ACTIVE);
}

export function updateActiveSpec(uid) {
  saveToLocalStorage(SKEY.ACTIVE, uid);
}

export function flushActiveSpec() {
  localStorage.removeItem(SKEY.ACTIVE);
}

// COLOR HISTORY
export function getColorHistory() {
  return getFromLocalStorage(SKEY.COLORHISTORY);
}

export function saveColorHistory(colors) {
  return saveToLocalStorage(SKEY.COLORHISTORY, colors);
}

// ARTBOARD PRESET
export function getArtboardPresets() {
  return getFromLocalStorage(SKEY.ARTBOARD_PRESET);
}

export function saveArtboardPresets(presets) {
  return saveToLocalStorage(SKEY.ARTBOARD_PRESET, presets);
}

// Preferences
export function getPreferences() {
  let pref = getFromLocalStorage(SKEY.PREFERENCE);
  if (pref) setPreferences(pref);
  else emptyPreferences();
}
export function emptyPreferences() {
  withPPI.set(prefDefaults.withPPI);
  showRuler.set(prefDefaults.showRuler);
  unlockNew.set(prefDefaults.unlockNew);
  responsiveDir.set(prefDefaults.responsiveDir);
  nRecommendations.set(prefDefaults.nRecommendations);
  pDrastic.set(prefDefaults.pDrastic);
  nMoreLikeThis.set(prefDefaults.nMoreLikeThis);
  lossWeight.set(prefDefaults.lossWeight);
  rationaleLevel.set(prefDefaults.rationaleLevel);
  storePreferences();
}
export function setPreferences(pref) {
  if (pref.withPPI) withPPI.set(pref.withPPI);
  if (pref.showRuler) showRuler.set(pref.showRuler);
  if (pref.unlockNew) unlockNew.set(pref.unlockNew);
  if (pref.responsiveDir) responsiveDir.set(pref.responsiveDir);
  if (pref.nRecommendations) nRecommendations.set(pref.nRecommendations);
  if (pref.nMoreLikeThis) nMoreLikeThis.set(pref.nMoreLikeThis);
  if (pref.pDrastic) pDrastic.set(pref.pDrastic);
  if (pref.lossWeight) lossWeight.set(pref.lossWeight);
  if (pref.rationaleLevel) rationaleLevel.set(prefDefaults.rationaleLevel);
  storePreferences();
}
export function updatePreference(key, value) {
  if (value === undefined) {
    console.warn("Wrong preference value: " + value);
  } else if (key === PrefKeys.withPPI) {
    if (value.constructor.name === "Boolean") withPPI.set(value);
    else console.warn("Wrong value for unlocking new artboards: " + value + ". It should be Boolean.");
  } else if (key === PrefKeys.showRuler) {
    if (value.constructor.name === "Boolean") showRuler.set(value);
    else console.warn("Wrong value for unlocking new artboards: " + value + ". It should be Boolean.");
  } else if (key === PrefKeys.unlockNew) {
    if (value.constructor.name === "Boolean") unlockNew.set(value);
    else console.warn("Wrong value for unlocking new artboards: " + value + ". It should be Boolean.");
  } else if (key === PrefKeys.responsiveDir) {
    if (ResponsiveDirections.includes(value)) responsiveDir.set(value);
    else console.warn("Wrong value for responsive design direction: " + value + ". It should be either 'large' or 'small'");
  } else if (key === PrefKeys.nRecommendations) {
    if (value.constructor.name === "Number" && !isNaN(value)) nRecommendations.set(value);
    else console.warn("Wrong value for the number of recommendations");
  } else if (key === PrefKeys.nMoreLikeThis) {
    if (value.constructor.name === "Number" && !isNaN(value)) nMoreLikeThis.set(value);
    else console.warn("Wrong value for the number of more-like-this-s");
  } else if (key === PrefKeys.pDrastic) {
    if (value.constructor.name === "Number" && !isNaN(value) && 0 < value && 1 > value) pDrastic.set(value);
    else console.warn("Wrong value for the ratio of drastic alternatives");
  } else if (key === PrefKeys.rationaleLevel) {
    if (value.constructor.name === "Number" && !isNaN(value) && 0 <= value && 2 >= value) rationaleLevel.set(value);
    else console.warn("Wrong value for the ratio of drastic alternatives");
  } else {
    console.warn("Wrong preference key: " + key);
  }
  storePreferences();
}
export function storePreferences() {
  let data = {
    withPPI: get(withPPI),
    showRuler: get(showRuler),
    unlockNew: get(unlockNew),
    responsiveDir: get(responsiveDir),
    nRecommendations: get(nRecommendations),
    nMoreLikeThis: get(nMoreLikeThis),
    pDrastic: get(pDrastic),
    lossWeight: get(lossWeight),
    rationaleLevel: get(rationaleLevel)
  };
  saveToLocalStorage(SKEY.PREFERENCE, data);
}

export function updateLossWeight(key, value) {
  let kb = key.split(".");
  lossWeight.update((weights) => {
    if (kb[0] === lossWeightKeys.identification) {
      if (kb.length == 1) {
        weights[lossWeightKeys.identification].weight = value;
      } else if (kb[1] === lossWeightKeys.position) {
        weights[lossWeightKeys.identification].by_channel[lossWeightKeys.position] = value;
      } else if (kb[1] === lossWeightKeys.length) {
        weights[lossWeightKeys.identification].by_channel[lossWeightKeys.length] = value;
      } else if (kb[1] === lossWeightKeys.color) {
        weights[lossWeightKeys.identification].by_channel[lossWeightKeys.color] = value;
      } else if (kb[1] === lossWeightKeys.size) {
        weights[lossWeightKeys.identification].by_channel[lossWeightKeys.size] = value;
      } else if (kb[1] === lossWeightKeys.shape) {
        weights[lossWeightKeys.identification].by_channel[lossWeightKeys.shape] = value;
      } else if (kb[1] === lossWeightKeys.text) {
        weights[lossWeightKeys.identification].by_channel[lossWeightKeys.text] = value;
      } else if (kb[1] === lossWeightKeys.others) {
        weights[lossWeightKeys.identification].by_channel[lossWeightKeys.others] = value;
      }
    } else if (kb[0] === lossWeightKeys.comparison) {
      if (kb.length == 1) {
        weights[lossWeightKeys.comparison].weight = value;
      } else if (kb[1] === lossWeightKeys.position) {
        weights[lossWeightKeys.comparison].by_channel[lossWeightKeys.position] = value;
      } else if (kb[1] === lossWeightKeys.length) {
        weights[lossWeightKeys.comparison].by_channel[lossWeightKeys.length] = value;
      } else if (kb[1] === lossWeightKeys.color) {
        weights[lossWeightKeys.comparison].by_channel[lossWeightKeys.color] = value;
      } else if (kb[1] === lossWeightKeys.size) {
        weights[lossWeightKeys.comparison].by_channel[lossWeightKeys.size] = value;
      } else if (kb[1] === lossWeightKeys.shape) {
        weights[lossWeightKeys.comparison].by_channel[lossWeightKeys.shape] = value;
      } else if (kb[1] === lossWeightKeys.text) {
        weights[lossWeightKeys.comparison].by_channel[lossWeightKeys.text] = value;
      } else if (kb[1] === lossWeightKeys.others) {
        weights[lossWeightKeys.comparison].by_channel[lossWeightKeys.others] = value;
      }
    } else if (kb[0] === lossWeightKeys.trend) {
      if (kb.length == 1) {
        weights[lossWeightKeys.trend].weight = value;
      } else if (kb[1] === lossWeightKeys.Y_X) {
        weights[lossWeightKeys.comparison].by_channel[lossWeightKeys.Y_X] = value;
      } else if (kb[1] === lossWeightKeys.COLOR_X_Y) {
        weights[lossWeightKeys.trend].by_channel[lossWeightKeys.COLOR_X_Y] = value;
      } else if (kb[1] === lossWeightKeys.SIZE_X_Y) {
        weights[lossWeightKeys.trend].by_channel[lossWeightKeys.SIZE_X_Y] = value;
      } else if (kb[1] === lossWeightKeys.COLOR_X) {
        weights[lossWeightKeys.trend].by_channel[lossWeightKeys.COLOR_X] = value;
      } else if (kb[1] === lossWeightKeys.others) {
        weights[lossWeightKeys.trend].by_channel[lossWeightKeys.others] = value;
      }
    } else if (kb[0] === lossWeightKeys.text) {
      if (kb.length == 1) {
        weights[lossWeightKeys.text].weight = value;
      }
    } else if (kb[0] === lossWeightKeys.density) {
      if (kb.length == 1) {
        weights[lossWeightKeys.density].weight = value;
      } else if (kb[1] === lossWeightKeys.occupied) {
        weights[lossWeightKeys.comparison].by_channel[lossWeightKeys.occupied] = value;
      } else if (kb[1] === lossWeightKeys.overplotted) {
        weights[lossWeightKeys.trend].by_channel[lossWeightKeys.overplotted] = value;
      }
    }
    return weights;
  });
  storePreferences();
}

// More-like-this
// Less-like this
export function loadRecStacks() {
  let mlt = getFromLocalStorage(SKEY.MLT) || [];
  let llt = getFromLocalStorage(SKEY.LLT) || [];
  moreLikeThisStack.set(mlt);
  lessLikeThisStack.set(llt);
}

export function clearRecStacks() {
  moreLikeThisStack.set([]);
  lessLikeThisStack.set([]);
}

export function saveToRecStacks(key, action, items) {
  if (key === SKEY.MLT) {
    moreLikeThisStack.update((stack) => {
      if (action === "add") { stack.push(...items); }
      else if (action === "remove") {
        stack = stack.filter((d) => !items.map(p => p.stack_id).includes(d.stack_id));
      }
      return stack;
    });
    saveToLocalStorage(SKEY.MLT, get(moreLikeThisStack));
  } else if (key === SKEY.LLT) {
    lessLikeThisStack.update((stack) => {
      if (action === "add") { stack.push(...items); }
      else if (action === "remove") {
        stack = stack.filter((d) => !items.map(p => p.stack_id).includes(d.stack_id));
      }
      return stack;
    });
    saveToLocalStorage(SKEY.LLT, get(lessLikeThisStack));
  }
}

export function queryRecStack(key, stack_id) {
  if (key === SKEY.MLT) {
    return get(moreLikeThisStack).filter((stack) => stack.stack_id === stack_id);
  } else if (key === SKEY.LLT) {
    return get(lessLikeThisStack).filter((stack) => stack.stack_id === stack_id);
  }
}

// UTILITYs
export function saveToLocalStorage(key, item) {
  localStorage.setItem(key, JSON.stringify(item));
}

export function getFromLocalStorage(key) {
  return JSON.parse(localStorage.getItem(key));
}

export function isClearLocal() {
  return !getFromLocalStorage(SKEY.DATA) && !getFromLocalStorage(SKEY.UIDS);
}

export function clearCacheAll() {
  // clear cache
  localStorage.removeItem(SKEY.DATA);
  let uids = getFromLocalStorage(SKEY.UIDS);
  uids?.forEach(uid => {
    localStorage.removeItem(uid);
  });
  localStorage.removeItem(SKEY.UIDS);
  localStorage.removeItem(SKEY.ACTIVE);
  localStorage.removeItem(SKEY.UNLOCKED);
  localStorage.removeItem(SKEY.COLORHISTORY);
  localStorage.removeItem(SKEY.MLT);
  localStorage.removeItem(SKEY.LLT);

  // clear store;
  callQueue.set(new DupoCallQueue());
  viewSpecs.set([]);
  data.set([]);
  dataInfo.set({});
  activeSpec.set(-1);
  unlockedSpecs.set([]);
  colorHistory.set([]);
  lessLikeThisStack.set([]);
  moreLikeThisStack.set([]);

  systemLog.set(new DupoSystemLog);
  defaultArtboardSets.set(veryDefaultArtboards);
  emptyPreferences();
  clearRecStacks();
}

// export-related
export async function exportLocalStorageAsJSON() {
  let output = {}
  output.data = getFromLocalStorage(SKEY.DATA) || null;
  output[SKEY.UIDS] = getFromLocalStorage(SKEY.UIDS) || null;
  output[SKEY.UIDS]?.forEach(uid => {
    output[uid] = getFromLocalStorage(uid) || null
  });
  output[SKEY.ACTIVE] = getFromLocalStorage(SKEY.ACTIVE) || null;
  output[SKEY.UNLOCKED] = getFromLocalStorage(SKEY.UNLOCKED) || null;
  output[SKEY.COLORHISTORY] = getFromLocalStorage(SKEY.COLORHISTORY) || null;
  output[SKEY.LOG] = getFromLocalStorage(SKEY.LOG) || null;
  output[SKEY.ARTBOARD_PRESET] = getFromLocalStorage(SKEY.ARTBOARD_PRESET) || null;
  output[SKEY.PREFERENCE] = getFromLocalStorage(SKEY.PREFERENCE) || null;
  return JSON.stringify(output, null, 2);
}

// import-related
export async function importFromJSON(inputJSON) {
  let input = (inputJSON.constructor.name === "Object" ? inputJSON : JSON.parse(inputJSON));
  let artboardPresets = get(defaultArtboardSets);
  if (input) {
    clearCacheAll();
    if (input.data) {
      saveToLocalStorage(SKEY.DATA, input.data);
      data.set(input.data);
    }
    if (input[SKEY.UIDS]) {
      saveToLocalStorage(SKEY.UIDS, input[SKEY.UIDS]);
      input[SKEY.UIDS].forEach(uid => {
        saveToLocalStorage(uid, input[uid]);
      });
      await getStoredSpecs();
    }
    if (input[SKEY.ACTIVE]) {
      saveToLocalStorage(SKEY.ACTIVE, input[SKEY.ACTIVE]);
      activeSpec.set(input[SKEY.ACTIVE]);
    }
    if (input[SKEY.UNLOCKED]) {
      saveToLocalStorage(SKEY.UNLOCKED, input[SKEY.UNLOCKED]);
      unlockedSpecs.set(input[SKEY.UNLOCKED]);
    }
    if (input[SKEY.COLORHISTORY]) {
      saveToLocalStorage(SKEY.COLORHISTORY, input[SKEY.COLORHISTORY]);
      colorHistory.set(input[SKEY.COLORHISTORY]);
    }
    if (input[SKEY.ARTBOARD_PRESET]) {
      saveToLocalStorage(SKEY.ARTBOARD_PRESET, input[SKEY.ARTBOARD_PRESET] || artboardPresets);
      defaultArtboardSets.set(input[SKEY.ARTBOARD_PRESET] || artboardPresets);
    }
    if (input[SKEY.LOG]) {
      saveToLocalStorage(SKEY.LOG, input[SKEY.LOG]);
      systemLog.set(new DupoSystemLog(input[SKEY.LOG].log_data));
    }
    if (input[SKEY.PREFERENCE]) {
      saveToLocalStorage(SKEY.PREFERENCE, input[SKEY.PREFERENCE]);
      setPreferences(input[SKEY.PREFERENCE])
    }
    let loadedViewSpecs = get(viewSpecs);
    duplicateDefaults(loadedViewSpecs);
  }
}

export function duplicateDefaults(loadedViewSpecs) {
  let loadedScreenTypes = loadedViewSpecs.map(d => d.artboard.screen_type);
  let firstSpec = loadedViewSpecs[0];
  get(defaultArtboardSets).forEach(async (preset, i) => {
    if (!loadedScreenTypes.includes(preset.name)) {
      firstSpec.duplicate({
        artboard_height: preset.artboard_height,
        artboard_width: preset.artboard_width,
        device_height: deviceTypes[preset.name].height,
        device_width: deviceTypes[preset.name].width,
        ppi: deviceTypes[preset.name].ppi,
        screen_type: preset.name,
        name: firstSpec.artboard.name + "-copy-" + i
      })
    }
  })
}